import { useEffect, useState, type ReactNode } from "react";
import { ArrowLeft, Lock, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils/cn";

import {
  useAddInternalNote,
  useAdminExpertDetailQuery,
  useDeleteInternalNote,
  useRemindExpert,
  useReviewDecision,
} from "../../api/hooks";
import type {
  AdminHistoryChange,
  AdminHistoryEntry,
  AdminEnjeu,
  AdminExpertDetail as Detail,
  AdminInternalNote,
  AdminMandate,
} from "../../types";
import { Badge } from "../ui/Badge";
import { ScopeFacets } from "../ui/ScopeFacets";
import { ErrorState, Loading } from "../ui/states";

const STATUS_TONE = {
  draft: "neutral",
  // Solid, as on the list and as in the Django detail header (`.badge-orange`):
  // "awaiting review" is the state this whole screen exists to act on.
  submitted: "attention",
  validated: "success",
  rejected: "destructive",
} as const;

function fmtDate(iso: string | null) {
  return iso ? iso.slice(0, 10) : null;
}
function fmtDateTime(iso: string | null) {
  return iso ? iso.slice(0, 16).replace("T", " ") : null;
}

export function AdminExpertDetail({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const query = useAdminExpertDetailQuery(id);
  const decision = useReviewDecision();
  const remind = useRemindExpert();
  // Reject composes a message to the expert in a modal; validate posts straight.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectMessage, setRejectMessage] = useState("");

  if (query.isPending) return <Loading />;
  if (query.isError || !query.data)
    return <ErrorState message={t("expert.error")} />;

  const expert = query.data as Detail;
  const name = expert.organization_name || t("expert.admin.noName");
  const pending = expert.has_pending_changes;
  // a finalized (validated/rejected) decision can be changed at
  // any time — only a draft cannot be decided.
  const decidable = expert.status !== "draft";

  const onDecided = {
    onSuccess: () => toast.success(t("expert.admin.detail.decisionDone")),
    onError: (err: unknown) => {
      const data = err as { detail?: string };
      toast.error(data.detail ?? t("expert.admin.detail.decisionError"));
    },
  };

  const validate = () =>
    decision.mutate({ id, decision: "validate" }, onDecided);

  const confirmReject = () =>
    decision.mutate(
      { id, decision: "reject", message: rejectMessage },
      {
        ...onDecided,
        onSuccess: () => {
          setRejectOpen(false);
          setRejectMessage("");
          toast.success(t("expert.admin.detail.decisionDone"));
        },
      },
    );

  const sendReminder = () =>
    remind.mutate(
      { id },
      {
        onSuccess: () =>
          toast.success(
            t("expert.admin.detail.reminderSent", {
              name: expert.organization_name || expert.email,
            }),
          ),
        onError: () => toast.error(t("expert.admin.detail.reminderError")),
      },
    );

  // meta line: NEQ · submitted timing · origin
  const submitted = fmtDate(expert.submitted_at ?? expert.declaration.declared_at);
  const metaBits = [
    expert.neq ? `${t("expert.admin.detail.neq")} ${expert.neq}` : null,
    submitted ? t("expert.admin.detail.submittedOn", { date: submitted }) : null,
    expert.invited_source_display,
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* sticky review bar */}
      <div className="sticky top-0 z-10 -mx-1 rounded-lg border border-border bg-card/95 p-4 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" aria-hidden />
            {t("expert.admin.detail.list")}
          </button>
          <div className="mr-auto min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate font-bold">{name}</span>
              <Badge tone={STATUS_TONE[expert.status]}>
                {t(`expert.admin.statuses.${expert.status}`)}
              </Badge>
              {pending && (
                <Badge tone="warning">{t("expert.admin.pendingChanges")}</Badge>
              )}
            </div>
            {metaBits.length > 0 && (
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {metaBits.join(" · ")}
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 gap-2">
            {/* Relance is available at any status — including a brouillon still
                being completed. */}
            <Button
              variant="outline"
              onClick={sendReminder}
              disabled={remind.isPending}
            >
              {t("expert.admin.detail.remind")}
            </Button>
            {decidable && (
              <>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    setRejectMessage(
                      pending ? expert.changes_message : expert.review_message,
                    );
                    setRejectOpen(true);
                  }}
                  disabled={decision.isPending}
                >
                  {pending
                    ? t("expert.admin.detail.rejectChanges")
                    : t("expert.admin.detail.reject")}
                </Button>
                <Button onClick={validate} disabled={decision.isPending}>
                  {pending
                    ? t("expert.admin.detail.validateChanges")
                    : expert.status === "validated"
                      ? t("expert.admin.detail.revalidate")
                      : t("expert.admin.detail.validate")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* decision history (once decided) */}
      {expert.reviewed_at && (
        <div
          className={cn(
            "rounded-lg border p-3 text-sm",
            expert.status === "validated"
              ? "border-success/30 bg-success/10"
              : "border-border bg-secondary/40",
          )}
        >
          <span className="font-bold">
            {t(`expert.admin.statuses.${expert.status}`)}
          </span>
          {expert.reviewed_by &&
            ` — ${t("expert.admin.detail.reviewedBy", { who: expert.reviewed_by })}`}
          {` · ${fmtDateTime(expert.reviewed_at)}`}
          {expert.status === "rejected" && expert.review_message && (
            <p className="mt-1 whitespace-pre-line">{expert.review_message}</p>
          )}
        </div>
      )}

      <InternalNotes id={id} notes={expert.internal_notes} />

      {rejectOpen && (
        <Modal
          title={
            pending
              ? t("expert.admin.detail.rejectChanges")
              : t("expert.admin.detail.reject")
          }
          onClose={() => setRejectOpen(false)}
        >
          <Label htmlFor="reject-message" className="text-xs text-muted-foreground">
            {t("expert.admin.detail.rejectMessageLabel")}
          </Label>
          <textarea
            id="reject-message"
            rows={4}
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              {t("expert.admin.detail.cancel")}
            </Button>
            <Button
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmReject}
              disabled={decision.isPending}
            >
              {pending
                ? t("expert.admin.detail.rejectChanges")
                : t("expert.admin.detail.reject")}
            </Button>
          </div>
        </Modal>
      )}

      <div className="grid gap-4 lg:grid-cols-12">
        {/* eligibility (tri-state) */}
        <Card className="lg:col-span-5">
          <CardHeader>
            <CardTitle className="text-base">
              {t("expert.admin.detail.eligibility")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Criterion
              tone={expert.eligibility.on_rena === true ? "no" : expert.eligibility.on_rena == null ? "unknown" : "ok"}
              title={
                expert.eligibility.on_rena === true
                  ? t("expert.admin.detail.renaListed")
                  : expert.eligibility.on_rena == null
                    ? t("expert.admin.detail.renaUnknown")
                    : t("expert.admin.detail.renaNotListed")
              }
              hint={
                expert.eligibility.on_rena === true
                  ? t("expert.admin.detail.renaListedHint")
                  : expert.eligibility.on_rena == null
                    ? t("expert.admin.detail.renaUnknownHint")
                    : t("expert.admin.detail.renaNotListedHint")
              }
            />
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span>{t("expert.admin.detail.completeness")}</span>
                <span className="font-semibold tabular-nums">
                  {expert.eligibility.completeness_pct}%
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${expert.eligibility.completeness_pct}%` }}
                />
              </div>
              {expert.eligibility.missing_labels.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("expert.admin.detail.completenessMissing", {
                    count: expert.eligibility.missing_labels.length,
                  })}{" "}
                  — {expert.eligibility.missing_labels.join(", ")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* declaration & provenance */}
        <Card className="lg:col-span-7">
          <CardHeader>
            <CardTitle className="text-base">
              {t("expert.admin.detail.declarationHeading")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t("expert.admin.detail.declarant")}</dt>
              <dd className="m-0">
                {expert.declaration.declarant_name || "—"}
                {expert.declaration.declarant_function &&
                  ` · ${expert.declaration.declarant_function}`}
              </dd>
              <dt className="text-muted-foreground">
                {t("expert.admin.detail.declarationDate")}
              </dt>
              <dd className="m-0">{fmtDate(expert.declaration.declared_at) ?? "—"}</dd>
              <dt className="text-muted-foreground">
                {t("expert.admin.detail.signatureConsent")}
              </dt>
              <dd className="m-0 flex flex-wrap gap-1.5">
                {expert.declaration.signature_confirmed && (
                  <Badge tone="success">
                    {t("expert.admin.detail.signatureConfirmed")}
                  </Badge>
                )}
                {expert.declaration.consent_display ? (
                  <Badge tone="success">{t("expert.admin.detail.consentDisplay")}</Badge>
                ) : (
                  <Badge tone="neutral">{t("expert.admin.detail.noConsent")}</Badge>
                )}
              </dd>
              <dt className="text-muted-foreground">{t("expert.admin.detail.invitedBy")}</dt>
              <dd className="m-0">{expert.declaration.invited_by ?? "—"}</dd>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* every submission (declarant) and CQDD decision — parity with the
          Django review page's Historique */}
      <HistoryCard entries={expert.history} />

      {/* organisation identity, contacts & profile — parity with the Django
          review page */}
      <IdentityProfileCards expert={expert} />

      {/* service scope — chosen green / not-chosen red */}
      {expert.scope_facets?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("expert.admin.detail.scope")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScopeFacets facets={expert.scope_facets} />
          </CardContent>
        </Card>
      )}

      {/* expertises */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("expert.admin.detail.enjeux")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {expert.enjeux.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t("expert.admin.detail.noEnjeux")}
            </p>
          )}
          {expert.enjeux.map((enjeu) => (
            <EnjeuBlock key={enjeu.issue.id} enjeu={enjeu} />
          ))}

          {/* Pre-approvals on enjeux the registration form never offers. No
              expertise row exists for them by design (one would be
              unsatisfiable in Part 2), so they are listed apart — otherwise a
              grant the expert can't see would be invisible to the reviewer too. */}
          {expert.hidden_preapproved.length > 0 && (
            <div className="rounded-lg border border-dashed p-3">
              <p className="text-sm font-semibold text-muted-foreground">
                {t("expert.admin.detail.hiddenPreapproved")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("expert.admin.detail.hiddenPreapprovedHelp")}
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {expert.hidden_preapproved.map((issue) => (
                  <li key={issue.id} className="flex flex-wrap items-center gap-2">
                    <Badge tone="neutral">{issue.thematic}</Badge>
                    <span>{issue.name}</span>
                    {issue.hidden_by_sector && (
                      <Badge tone="warning">
                        {t("expert.admin.detail.outsideSectors")}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(expert.orphan_actions.length > 0 || expert.orphan_mandates.length > 0) && (
            <div className="rounded-lg border border-dashed p-3">
              <p className="text-sm font-semibold text-muted-foreground">
                {t("expert.admin.detail.orphans")}
              </p>
              <ul className="ml-4 mt-2 list-disc text-sm text-muted-foreground">
                {expert.orphan_actions.map((a) => (
                  <li key={a.id} className={cn(!a.active && "line-through opacity-60")}>
                    {a.name}
                  </li>
                ))}
                {expert.orphan_mandates.map((m, i) => (
                  <li key={`m-${i}`}>{m.name}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Private CQDD notes on the candidature — never emailed. Addable at any status
 * (including brouillon); distinct from the reject message sent to the expert. */
function InternalNotes({
  id,
  notes,
}: {
  id: string;
  notes: AdminInternalNote[];
}) {
  const { t } = useTranslation();
  const add = useAddInternalNote();
  const remove = useDeleteInternalNote();
  const [body, setBody] = useState("");
  // Only the 3 most recent notes show inline; the rest live behind a modal.
  const [showAll, setShowAll] = useState(false);
  // The note whose removal awaits the administrator's confirmation.
  const [pendingRemoval, setPendingRemoval] = useState<AdminInternalNote | null>(null);
  // Whether that removal was started from the « see all » modal, which is
  // closed while the confirmation is open and comes back on cancel.
  const [askedFromAll, setAskedFromAll] = useState(false);

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    remove.mutate(
      { id, noteId: pendingRemoval.id },
      {
        onSuccess: () => toast.success(t("expert.admin.detail.notes.removed")),
        onError: () => toast.error(t("expert.admin.detail.notes.removeError")),
        onSettled: () => setPendingRemoval(null),
      },
    );
  };
  // Close the "see all" modal once deletions bring the count back to the cap —
  // the trigger is gone by then, so an open modal would just be a stale overlay.
  useEffect(() => {
    if (notes.length <= 3) setShowAll(false);
  }, [notes.length]);

  const submit = () => {
    const value = body.trim();
    if (!value) return;
    add.mutate(
      { id, body: value },
      {
        onSuccess: () => setBody(""),
        onError: () => toast.error(t("expert.admin.detail.notes.error")),
      },
    );
  };

  const noteItem = (note: AdminInternalNote) => (
    <li
      key={note.id}
      className="flex items-start gap-2 border-t border-border pt-3"
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">
          {note.author ?? t("expert.admin.detail.notes.unknownAuthor")}
          {` · ${fmtDateTime(note.created_at)}`}
        </div>
        {note.body ? (
          <p className="mt-1 whitespace-pre-line text-sm">{note.body}</p>
        ) : (
          <p className="mt-1 text-sm italic text-muted-foreground">
            {t("expert.admin.detail.notes.anonymized")}
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-muted-foreground"
        onClick={() => {
          // Removing hides the note and logs it in the Historique; ask first,
          // in the page's own dialog, as the Django page does. The « see all »
          // modal closes so the two dialogs never stack.
          setAskedFromAll(showAll);
          setShowAll(false);
          setPendingRemoval(note);
        }}
        disabled={remove.isPending}
        aria-label={t("expert.admin.detail.notes.delete")}
      >
        <Trash2 className="size-4" aria-hidden />
      </Button>
    </li>
  );

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lock className="size-4 text-muted-foreground" aria-hidden />
          {t("expert.admin.detail.notes.title")}
          <Badge tone="neutral">{t("expert.admin.detail.notes.private")}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label
            htmlFor="internal-note"
            className="text-xs text-muted-foreground"
          >
            {t("expert.admin.detail.notes.addLabel")}
          </Label>
          <textarea
            id="internal-note"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              onClick={submit}
              disabled={add.isPending || !body.trim()}
            >
              {t("expert.admin.detail.notes.add")}
            </Button>
          </div>
        </div>

        {notes.length > 0 && (
          <>
            <ul className="space-y-3">{notes.slice(0, 3).map(noteItem)}</ul>
            {notes.length > 3 && (
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setShowAll(true)}
              >
                {t("expert.admin.detail.notes.seeAll", { count: notes.length })}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>

    {showAll && (
      <Modal
        title={t("expert.admin.detail.notes.title")}
        onClose={() => setShowAll(false)}
      >
        <ul className="space-y-3">{notes.map(noteItem)}</ul>
      </Modal>
    )}

    {pendingRemoval && (
      <ConfirmDialog
        title={t("expert.admin.detail.notes.confirmRemove")}
        message={t("expert.admin.detail.notes.confirmRemoveBody")}
        confirmLabel={t("expert.admin.detail.notes.delete")}
        cancelLabel={t("expert.admin.detail.cancel")}
        onConfirm={confirmRemoval}
        onClose={() => {
          setPendingRemoval(null);
          if (askedFromAll) setShowAll(true);
        }}
        busy={remove.isPending}
      />
    )}
    </>
  );
}

function Criterion({
  tone,
  title,
  hint,
}: {
  tone: "ok" | "no" | "unknown";
  title: string;
  hint?: string;
}) {
  const glyph = tone === "ok" ? "✓" : tone === "no" ? "✗" : "?";
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          "grid size-6 flex-none place-items-center rounded-full text-sm font-bold",
          tone === "ok" && "bg-success text-success-foreground",
          tone === "no" && "bg-destructive text-destructive-foreground",
          tone === "unknown" && "bg-secondary text-muted-foreground",
        )}
      >
        {glyph}
      </span>
      <div>
        <strong className="text-sm">{title}</strong>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </div>
  );
}

/** The review history: submissions (with the declarant as it stood then), a
 * validated expert's change requests, and CQDD decisions with the
 * administrator — the Django page's « Historique », from the same builder. */
/** What one history entry changed — the same rows the Django page renders
 *  (_history_changes.html): added items green, removed ones struck through in
 *  red, a plain field's old value struck through before the new one. */
function HistoryChanges({ changes }: { changes: AdminHistoryChange[] }) {
  const { t } = useTranslation();
  if (changes.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5 pl-3 text-xs">
      {changes.map((row, i) => (
        <li key={i}>
          <span className="text-muted-foreground">
            {t("expert.admin.history.labelColon", { label: row.label })}
          </span>
          {row.added.map((item) => (
            <span key={`+${item}`} className="ml-1 text-success-ink">
              + {item}
            </span>
          ))}
          {row.removed.map((item) => (
            <del key={`-${item}`} className="ml-1 text-destructive-ink">
              {item}
            </del>
          ))}
          {(row.old != null || row.new != null) && (
            <>
              <del className="ml-1 text-destructive-ink">{row.old}</del>{" "}
              <span aria-hidden>→</span>
              <span className="sr-only">{t("expert.admin.history.becomes")}</span>{" "}
              <span className="text-success-ink">{row.new}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function HistoryCard({ entries }: { entries: AdminHistoryEntry[] }) {
  const { t } = useTranslation();
  const h = (k: string, o?: Record<string, string>) => t(`expert.admin.history.${k}`, o);
  const title = (entry: AdminHistoryEntry) => {
    if (entry.kind === "submission") return h("submission");
    if (entry.kind === "changes_submitted") return h("changesSubmitted");
    if (entry.kind === "edit") return h("edit");
    if (entry.kind === "note_removed") return h("noteRemoved");
    if (entry.revalidation)
      return entry.outcome === "validated" ? h("changesValidated") : h("changesRejected");
    return entry.outcome === "validated" ? h("validated") : h("rejected");
  };
  return (
    <Card data-testid="review-history">
      <CardHeader>
        <CardTitle className="text-base">{h("heading")}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{h("empty")}</p>
        ) : (
          <ul className="divide-y text-sm">
            {entries.map((entry, i) => (
              <li key={i} className="py-2 first:pt-0 last:pb-0">
                <span className="mr-2 text-xs tabular-nums text-muted-foreground">
                  {entry.at.slice(0, 16).replace("T", " ")}
                </span>
                <strong>{title(entry)}</strong>
                {entry.kind === "submission" && entry.declarant_name && (
                  <>
                    {" — "}
                    {h("declarant", { name: entry.declarant_name })}
                    {entry.declarant_function && ` · ${entry.declarant_function}`}
                  </>
                )}
                {entry.kind !== "submission" && entry.by && (
                  <> — {h("by", { name: entry.by })}</>
                )}
                {entry.kind === "decision" && entry.message && (
                  <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">
                    {entry.message}
                  </p>
                )}
                {entry.kind === "note_removed" && (
                  <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">
                    {entry.note_author && `${h("noteBy", { name: entry.note_author })} `}
                    {entry.note_body ? (
                      <del>{entry.note_body}</del>
                    ) : (
                      <span className="italic">{h("noteAnonymized")}</span>
                    )}
                  </p>
                )}
                {(entry.kind === "decision" ||
                  entry.kind === "edit" ||
                  entry.kind === "changes_submitted") && (
                  <HistoryChanges changes={entry.changes ?? []} />
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Organisation identity, contacts and description — mirrors the Django review
 * page's _profile_summary so items 1/3/6/8 land on the SPA reviewer surface too.
 * Hyperlinks use the economic-theme ink (blue-family, never red — item 6). */
function IdentityProfileCards({ expert }: { expert: Detail }) {
  const { t } = useTranslation();
  const f = (k: string) => t(`expert.register.fields.${k}`);
  const { identity, org_profile } = expert;
  const link = "text-theme-economic-ink hover:underline";
  const rows: [string, ReactNode][] = [];
  if (identity.legal_name_req) rows.push([f("legal_name_req"), identity.legal_name_req]);
  if (identity.email)
    rows.push([
      f("email"),
      <a key="e" href={`mailto:${identity.email}`} className={link}>
        {identity.email}
      </a>,
    ]);
  if (identity.phone) rows.push([f("phone"), identity.phone]);
  if (identity.website)
    rows.push([
      f("website"),
      <a key="w" href={identity.website} target="_blank" rel="noopener" className={link}>
        {identity.website}
      </a>,
    ]);
  // Same labels as the Django review page's _profile_summary.
  const loc = (k: string) => t(`expert.dashboard.detail.${k}`);
  if (identity.head_office_address || identity.head_office_region)
    rows.push([
      loc("headOffice"),
      <span key="ho">
        {identity.head_office_address || "—"}
        {identity.head_office_region && (
          <span className="block text-xs text-muted-foreground">
            {identity.head_office_region}
            {identity.head_office_mrc && ` — ${identity.head_office_mrc}`}
          </span>
        )}
      </span>,
    ]);
  if (identity.business_locations.length > 0)
    rows.push([
      loc("otherLocations"),
      <span key="bl">
        {identity.business_locations.map((place, i) => (
          <span key={i} className="block">
            {place.address ? (
              <>
                {place.address}
                <span className="block text-xs text-muted-foreground">
                  {place.region} — {place.mrc}
                </span>
              </>
            ) : (
              `${place.region} — ${place.mrc}`
            )}
          </span>
        ))}
      </span>,
    ]);
  if (identity.serves_other_provinces != null)
    rows.push([
      loc("otherProvinces"),
      identity.serves_other_provinces
        ? identity.other_provinces.join(", ")
        : t("expert.register.no"),
    ]);

  // `loc`, not `f`: this is the CQDD's review page, and it reads the short
  // labels of the Django partial it mirrors (`_profile_summary.html`), not the
  // longer question wording the registration form asks with.
  const profRows: [string, ReactNode][] = [];
  if (org_profile.founding_year != null)
    profRows.push([loc("foundingYear"), org_profile.founding_year]);
  if (org_profile.dd_services_start_year != null)
    profRows.push([loc("ddStart"), org_profile.dd_services_start_year]);
  if (org_profile.total_employees != null)
    profRows.push([loc("employees"), org_profile.total_employees]);
  if (org_profile.dedicated_experts_count != null)
    profRows.push([loc("dedicated"), org_profile.dedicated_experts_count]);
  if (org_profile.awards) profRows.push([loc("awards"), org_profile.awards]);
  if (org_profile.affiliations)
    profRows.push([loc("affiliations"), org_profile.affiliations]);

  return (
    <div className="grid gap-4 lg:grid-cols-12">
      <Card className="lg:col-span-6">
        <CardHeader>
          <CardTitle className="text-base">
            {t("expert.admin.detail.identityHeading")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {rows.length > 0 && (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              {rows.map(([label, value], i) => (
                <div key={`${label}-${i}`} className="contents">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="m-0 break-words">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          {identity.contacts.map((c, i) =>
            c.first_name || c.last_name ? (
              <div key={i} className="border-t border-border pt-2 text-sm">
                <p className="font-medium">
                  {c.first_name} {c.last_name}
                  {c.function && (
                    <span className="text-muted-foreground"> — {c.function}</span>
                  )}
                </p>
                {c.email && (
                  <a href={`mailto:${c.email}`} className={`block ${link}`}>
                    {c.email}
                  </a>
                )}
                {c.phone && (
                  <span>
                    {c.phone}
                    {c.extension && ` #${c.extension}`}
                  </span>
                )}
                {c.linkedin && (
                  <a
                    href={c.linkedin}
                    target="_blank"
                    rel="noopener"
                    className={`block ${link}`}
                  >
                    LinkedIn
                  </a>
                )}
              </div>
            ) : null,
          )}
        </CardContent>
      </Card>

      {(profRows.length > 0 || org_profile.description) && (
        <Card className="lg:col-span-6">
          <CardHeader>
            <CardTitle className="text-base">
              {t("expert.admin.detail.profileHeading")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {/* description first. */}
            {org_profile.description && (
              <p className="whitespace-pre-line">{org_profile.description}</p>
            )}
            {profRows.length > 0 && (
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
                {profRows.map(([label, value], i) => (
                  <div key={`${label}-${i}`} className="contents">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="m-0">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function EnjeuBlock({ enjeu }: { enjeu: AdminEnjeu }) {
  const { t } = useTranslation();
  const mandate = enjeu.mandate as AdminMandate | null;
  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{enjeu.issue.name}</span>
        {enjeu.preapproved && (
          <Badge tone="success">{t("expert.admin.detail.preapproved")}</Badge>
        )}
        {enjeu.is_new && <Badge tone="warning">{t("expert.admin.detail.isNew")}</Badge>}
        {enjeu.hidden_by_sector && (
          <Badge tone="warning">{t("expert.admin.detail.outsideSectors")}</Badge>
        )}
        {enjeu.mandate_changed && (
          <Badge tone="warning">{t("expert.admin.detail.mandateChanged")}</Badge>
        )}
        {enjeu.mandate_count != null && (
          <Badge tone="neutral">
            {t("expert.admin.detail.mandateCount", { count: enjeu.mandate_count })}
          </Badge>
        )}
      </div>

      {(enjeu.scope_changes ?? []).length > 0 && (
        <div className="mt-3 text-sm">
          <p className="font-medium">{t("expert.admin.detail.scopeChanged")}</p>
          <HistoryChanges changes={enjeu.scope_changes} />
        </div>
      )}

      {enjeu.objectives.length > 0 && (
        <div className="mt-3 space-y-2 text-sm">
          <p className="font-medium">{t("expert.admin.detail.objectives")}</p>
          {enjeu.objectives.map((obj) => (
            <div key={obj.goal.id} className="ml-3">
              <p className={cn(obj.hidden_by_sector && "text-muted-foreground")}>
                {obj.goal.name}
                {obj.hidden_by_sector && (
                  <span className="ml-2 text-xs italic">
                    {t("expert.admin.detail.outsideSectorsNote")}
                  </span>
                )}
              </p>
              <ul className="ml-4 list-disc text-muted-foreground">
                {obj.actions.map((action) => (
                  <li
                    key={action.id}
                    className={cn(
                      !action.active && "line-through opacity-60",
                      action.hidden_by_sector && "opacity-60",
                    )}
                  >
                    {action.name}
                    {!action.active && (
                      <Badge tone="neutral" className="ml-2">
                        {t("expert.admin.detail.removed")}
                      </Badge>
                    )}
                    {action.hidden_by_sector && (
                      <span className="ml-2 text-xs italic">
                        {t("expert.admin.detail.outsideSectorsNote")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {mandate && (
        <div className="mt-3 rounded-md border bg-secondary/30 p-3 text-sm">
          <p className="font-semibold">
            {mandate.name}
            {mandate.year != null && (
              <span className="ml-2 font-normal text-muted-foreground">
                {t("expert.admin.detail.mandateYear", { year: mandate.year })}
              </span>
            )}
          </p>
          {mandate.description && (
            <p className="mt-1 whitespace-pre-line text-muted-foreground">
              {mandate.description}
            </p>
          )}
          {mandate.clients.length > 0 && (
            <>
              <p className="mt-2.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {t("expert.admin.detail.clients")}
              </p>
              <ul className="ml-4 list-disc">
                {mandate.clients.map((c, i) => (
                  <li key={i}>
                    <span className="font-medium">
                      {c.org_name || t("expert.admin.detail.clientNoName")}
                    </span>
                    <span className="text-muted-foreground">
                      {(c.contact_name || c.email || c.phone) && " — "}
                      {c.contact_name}
                      {c.contact_name && (c.email || c.phone) && " · "}
                      {c.email}
                      {c.email && c.phone && " · "}
                      {c.phone}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
